import { useEffect, useRef, useState } from "react";
import Button from "../Button/Button";
import IconNew from "../Icon/Icon";
import { useMediaQuery } from "react-responsive";
import { SMALL_SCREEN } from "../../../Utils/generic";

export default function ScrollContainer({
  children,
  theme,
  arrowInMobile = false,
  insideArrows = false,
  itemSize: propItemSize = 200,
  carousel = false,
  carouselDelay = 5000,
}: {
  children: JSX.Element;
  theme?: "dark" | "light" | "light2";
  arrowInMobile?: boolean;
  insideArrows?: boolean;
  itemSize?: number;
  carousel?: boolean;
  carouselDelay?: number;
}) {
  const [showArrows, setShowArrows] = useState(true);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [itemSize, setItemSize] = useState(propItemSize);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Calculate dynamic itemSize from first child element if available
    if (container.children.length > 0 && container.firstElementChild) {
      const firstChildWidth = container.firstElementChild.clientWidth;
      if (firstChildWidth > 0) {
        setItemSize(firstChildWidth);
      }

      // Calculate total number of slides
      const slideWidth = firstChildWidth > 0 ? firstChildWidth : propItemSize;
      const totalWidth = container.scrollWidth;
      const calculatedTotalSlides = Math.ceil(totalWidth / slideWidth);
      setTotalSlides(calculatedTotalSlides);

      // Initial check for arrows visibility
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft + container.offsetWidth < container.scrollWidth,
      );
    }

    const handleScroll = () => {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft + container.offsetWidth < container.scrollWidth,
      );

      // Update current slide based on scroll position
      if (container.children.length > 0 && container.firstElementChild) {
        const slideWidth = container.firstElementChild.clientWidth;
        const currentSlideIndex = Math.round(container.scrollLeft / slideWidth);
        setCurrentSlide(currentSlideIndex);
      }
    };

    handleScroll(); // Check on mount

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    // Observe container resize to ensure arrows are recalculated when content changes (e.g. images load)
    const resizeObserver = new ResizeObserver(() => {
      handleScroll();

      // Also update the arrows visibility after content changes
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft + container.offsetWidth < container.scrollWidth,
      );
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    goToSlide(0);
  }, [children]);

  useEffect(() => {
    const checkArrows = () => {
      const container = containerRef.current;
      if (!container) return;

      const isMobile = window.innerWidth <= 768;
      const shouldShowArrows =
        container.offsetWidth < container.scrollWidth &&
        (arrowInMobile || !isMobile);
      setShowArrows(shouldShowArrows);

      // Also update left/right arrows visibility
      if (shouldShowArrows) {
        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(
          container.scrollLeft + container.offsetWidth < container.scrollWidth,
        );
      }
    };

    // Initial check on mount with a small delay to ensure content is rendered
    checkArrows();

    // Run another check after a short delay to catch any late-rendered content
    const initialCheckTimeout = setTimeout(checkArrows, 50);

    // Continue checking periodically
    const interval = setInterval(checkArrows, 100);

    return () => {
      clearInterval(interval);
      clearTimeout(initialCheckTimeout);
    };
  }, [arrowInMobile]);

  // Carousel functionality
  useEffect(() => {
    if (carousel && containerRef.current) {
      // Clear any existing interval
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }

      // Set up carousel interval
      carouselIntervalRef.current = setInterval(() => {
        const container = containerRef.current;
        if (!container) return;

        // If we're at the end, go back to the beginning
        if (currentSlide >= totalSlides - 1) {
          container.scrollTo({
            left: 0,
            behavior: "auto",
          });
        } else {
          // Otherwise go to the next slide
          container.scrollBy({
            left: itemSize,
            behavior: "auto",
          });
        }
      }, carouselDelay);
    }

    // Clean up interval on unmount
    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, [carousel, carouselDelay, currentSlide, totalSlides, itemSize]);

  // Function to navigate to a specific slide
  const goToSlide = (slideIndex: number) => {
    if (
      containerRef.current &&
      containerRef.current.children.length > 0 &&
      containerRef.current.firstElementChild
    ) {
      const slideWidth = containerRef.current.firstElementChild.clientWidth;
      containerRef.current.scrollTo({
        left: slideWidth * slideIndex,
        behavior: "auto",
      });
    }
  };

  // Function to navigate to the previous slide with loop
  const goPrevious = () => {
    if (currentSlide <= 0) {
      // If at the beginning, loop to the end
      goToSlide(totalSlides - 1);
    } else {
      // Otherwise go to the previous slide
      goToSlide(currentSlide - 1);
    }
  };

  // Function to navigate to the next slide with loop
  const goNext = () => {
    if (currentSlide >= totalSlides - 1) {
      // If at the end, loop to the beginning
      goToSlide(0);
    } else {
      // Otherwise go to the next slide
      goToSlide(currentSlide + 1);
    }
  };

  return (
    <div className="w100 d-flex flex-column align-items-center gap-1 position-relative">
      <div className="w-100 d-flex align-items-center position-relative">
        {showArrows && showLeftArrow && !carousel && (
          <div
            className="position-absolute top-50 cursor-pointer p-2"
            style={{
              marginTop: "-20px",
              backgroundColor: !insideArrows
                ? ""
                : theme === "light"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(255, 255, 255, 0.8)",
              borderRadius: "100%",
              left: insideArrows ? "10px" : "-40px",
              zIndex: 5,
            }}
            onClick={() => {
              carousel
                ? goPrevious()
                : containerRef.current?.scrollBy({
                    left: -itemSize,
                    behavior: "smooth",
                  });
            }}
          >
            <IconNew
              icon="arrow_left_alt"
              color={theme === "light" ? "white" : "black"}
              className="cursor-pointer"
            />
          </div>
        )}
        {carousel && (
          <div
            style={{
              zIndex: 1,
              height: "calc(100% - 60px)",
              bottom: "0px",
              left: "0px",
            }}
            className="position-absolute w-50"
            onClick={() => {
              goPrevious();
            }}
          >
            {!isSmallScreen && (
              <IconNew
                icon="arrow_left_alt"
                color="white"
                className="position-absolute top-50 cursor-pointer"
                style={{
                  left: "20px",
                }}
              />
            )}
          </div>
        )}
        <div
          style={{
            width: arrowInMobile ? "calc(100% - 10px)" : "100%",
            overflowX: carousel ? "hidden" : "scroll",
          }}
        >
          <div
            ref={containerRef}
            className="multi-select-container gap-2"
            style={{
              scrollSnapType: "x mandatory",
              overflowX: carousel ? "hidden" : "scroll",
            }}
          >
            {children}
          </div>
        </div>
        {carousel && (
          <div
            style={{
              zIndex: 1,
              height: "calc(100% - 60px)",
              bottom: "0px",
              right: "0px",
            }}
            className="position-absolute w-50"
            onClick={() => {
              goNext();
            }}
          >
            {!isSmallScreen && (
              <IconNew
                icon="arrow_right_alt"
                color="white"
                className="position-absolute top-50 cursor-pointer"
                style={{
                  right: "20px",
                }}
              />
            )}
          </div>
        )}
        {showArrows && showRightArrow && !carousel && (
          <div
            className="position-absolute top-50 cursor-pointer p-2"
            style={{
              marginTop: "-20px",
              backgroundColor: !insideArrows
                ? ""
                : theme === "light"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(255, 255, 255, 0.8)",
              borderRadius: "100%",
              right: insideArrows ? "10px" : "-40px",
              zIndex: 5,
            }}
            onClick={() => {
              carousel
                ? goNext()
                : containerRef.current?.scrollBy({
                    left: itemSize,
                    behavior: "smooth",
                  });
            }}
          >
            <IconNew
              icon="arrow_right_alt"
              color={theme === "light" ? "white" : "black"}
              className="cursor-pointer"
            />
          </div>
        )}
      </div>

      {/* Slide indicators */}
      {carousel && totalSlides > 1 && (
        <div className="indicators-cont px-2 position-absolute top-0 indicator d-flex justify-content-center w-100 mt-4 gap-2">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <div
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: `calc(95% / ${totalSlides})`,
                height: "4px",
                borderRadius: "0",
                backgroundColor:
                  currentSlide === index ? "#fff" : "rgba(255, 255, 255, 0.5)",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
